import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { getIn, useFormikContext } from 'formik'
import { Fragment, useState } from 'react'
import styled from 'styled-components'

import { globalDefinitions } from '@/defined-translations'
import { StSelectListContainer } from '../questionnaire-question/QuestionnaireQuestion.styled'
import { SelectListItem } from '../select-list-item'

import type {
  IQuestionnaireQuestion,
  MultiSelectCommentAnswerItem,
  MultiSelectCommentAnswerOption,
} from '@healthblocks-io/core'

type MultiSelectCommentQuestionProps = {
  data: IQuestionnaireQuestion
  questionId: string
}

/**
 * Multi-select where individual options can carry a free-text comment.
 * The answer value is a single Formik field holding
 * MultiSelectCommentAnswerItem[] ({ value, comment? }) — selecting an option
 * appends an item, deselecting removes it (comment included), and typing in
 * the comment area updates the item in place.
 */
const MultiSelectCommentQuestion = ({ data, questionId }: MultiSelectCommentQuestionProps) => {
  const { setFieldValue, values } = useFormikContext<Record<string, unknown>>()

  // Comment inputs the user has blurred at least once. The required-comment
  // error only shows after blur — showing it the instant the option is
  // selected (while the empty input has focus) would flag a mistake the user
  // hasn't had the chance to make yet. Validity itself is not affected:
  // the Yup schema keeps "Ga verder" disabled regardless of touched state.
  //
  // This is deliberately local state instead of Formik's touched mechanism,
  // which cannot work here for two reasons:
  // 1. The questionnaire auto-submits every change, and <Formik
  //    enableReinitialize> re-initializes from engine state on each submit —
  //    resetForm wipes `touched`, so a Formik-touched error would flicker off
  //    on every keystroke. Component state survives those reinitializations
  //    (the component only remounts when the question changes).
  // 2. Formik touched paths for arrays are positional ([1].comment) while the
  //    answer items shift index on (de)selection; we key by option value so
  //    the error stays attached to the right input.
  const [touchedComments, setTouchedComments] = useState<Set<string>>(() => new Set())

  // Use getIn to support dot-path field names inside FieldArrays
  const currentValue = getIn(values, questionId)
  const selectedItems: MultiSelectCommentAnswerItem[] = Array.isArray(currentValue) ? currentValue : []

  const answerOptions = (data.answerOption ?? []) as MultiSelectCommentAnswerOption[]

  const maxReached = data.max !== undefined && selectedItems.length >= data.max

  const isSelected = (optionValue: string) => selectedItems.some((item) => item.value === optionValue)

  const handleOptionClick = (clickedValue: string) => {
    const clickedOption = answerOptions.find((option) => option.value === clickedValue)
    if (!clickedOption) return

    let newItems: MultiSelectCommentAnswerItem[]

    // If deselecting, remove the item (its comment goes with it) and reset
    // the touched state so re-selecting starts with a clean slate
    if (isSelected(clickedValue)) {
      newItems = selectedItems.filter((item) => item.value !== clickedValue)
      setTouchedComments((touched) => {
        const next = new Set(touched)
        next.delete(clickedValue)
        return next
      })
    }
    // If selecting an exclusive option, clear all others
    else if (clickedOption.exclusive) {
      newItems = [{ value: clickedValue }]
    }
    // If selecting a regular option, remove any exclusive options first
    else {
      if (maxReached) return

      const exclusiveValues = new Set(answerOptions.filter((option) => option.exclusive).map((option) => option.value))

      newItems = [...selectedItems.filter((item) => !exclusiveValues.has(item.value)), { value: clickedValue }]
    }

    setFieldValue(questionId, newItems)
  }

  const handleCommentChange = (optionValue: string, text: string) => {
    setFieldValue(
      questionId,
      selectedItems.map((item) =>
        item.value === optionValue
          ? // Keep the text verbatim while typing; drop the key when cleared.
            // Whitespace-only comments are normalized away on submit.
            text === ''
            ? { value: item.value }
            : { value: item.value, comment: text }
          : item,
      ),
    )
  }

  return (
    <StSelectListContainer>
      {answerOptions.map((option) => {
        const selectedItem = selectedItems.find((item) => item.value === option.value)
        const selected = selectedItem !== undefined
        const isDisabled = !selected && maxReached && !option.exclusive

        const showCommentArea = option.showCommentArea || option.isCommentRequired
        const missingRequiredComment = !!option.isCommentRequired && !selectedItem?.comment?.trim()
        // Only surface the error after the user left the field (blur)
        const showCommentError = missingRequiredComment && touchedComments.has(option.value ?? '')

        return (
          <Fragment key={`${questionId}_${option.value}`}>
            <SelectListItem
              type='checkbox'
              id={`${questionId}_${option.value}`}
              name={questionId}
              value={option.value}
              label={option.label}
              checked={selected}
              disabled={isDisabled}
              onChange={() => handleOptionClick(option.value ?? '')}
            />
            {showCommentArea && selected && (
              <>
                <StCommentInput
                  id={`${questionId}_${option.value}_comment`}
                  type='text'
                  value={selectedItem?.comment ?? ''}
                  onChange={(event_) => handleCommentChange(option.value ?? '', event_.target.value)}
                  onBlur={() => setTouchedComments((touched) => new Set(touched).add(option.value ?? ''))}
                  placeholder={
                    option.commentPlaceholder ??
                    t({
                      id: 'questionnaire.comment.placeholder',
                      message: 'Specifieer hier...',
                    })
                  }
                  aria-label={`${option.label} - toelichting`}
                  $error={showCommentError}
                  autoFocus
                />
                {showCommentError && <StCommentError>{i18n._(globalDefinitions.commentRequired)}</StCommentError>}
              </>
            )}
          </Fragment>
        )
      })}
    </StSelectListContainer>
  )
}

export { MultiSelectCommentQuestion }

const StCommentInput = styled.input<{ $error?: boolean }>`
  width: 100%;
  min-height: 57px;
  border: 1px solid
    ${({ theme, $error }) => ($error ? theme.Colors.Status.Error : theme.Components.Conversational.Border)};
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Small}px;
  padding: ${({ theme }) => `${theme.UI.SpacingPx.Space3} ${theme.UI.SpacingPx.Space4}`};
  font-size: inherit;
  font-family: inherit;
  outline: none;

  &:focus {
    border-color: ${({ theme, $error }) =>
      $error ? theme.Colors.Status.Error : theme.Components.Conversational.ActiveBorder};
  }
`

const StCommentError = styled.span`
  color: ${({ theme }) => theme.Colors.Status.Error};
  font-size: 14px;
`
